import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import OperatorAppLink from '@/utils/operatorApp';
import { opBehaviour } from '@/pages/inspection/api';
import PropTypes from 'prop-types';
import { Tag, Tooltip, Descriptions, Empty, Spin, Card, Button } from 'antd';
import DataTable from '@/components/DataTable';
import PageModal from '@/components/PageModal';
import Icons from '@/components/third-party/Icons';
import { get_batch_process_operations } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import StatusIndicator from '@/components/genealogy/StatusIndicator';
import { fmt, yesNo, StatusBadge, personName, formatSeconds } from './helpers';
import OperationDetailPanel from './OperationDetailPanel';
import { useMbmrAlias, useIngredientsAlias } from '@/hooks/useAppSetting';

// `formatSeconds` in ./helpers renders everything in minutes ("592m 30s"), which is unreadable
// for the multi-hour standards the MBMR now carries — roll hours up locally instead.
const dur = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return '—';
  const s = Math.round(Number(seconds));
  if (Number.isNaN(s)) return '—';
  const sign = s < 0 ? '-' : '';
  const a = Math.abs(s);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  if (h) return `${sign}${h}h ${m}m`;
  if (m) return `${sign}${m}m ${a % 60}s`;
  return `${sign}${a}s`;
};

// Standard vs actual, with the signed variance. Renders nothing when there is no standard.
const VarianceTag = ({ variance }) => {
  if (!variance || variance.seconds === null || variance.seconds === undefined) return null;
  const over = variance.direction === 'over';
  const on = variance.direction === 'on_standard';
  return (
    <Tag color={on ? 'green' : over ? 'red' : 'blue'} style={{ marginInlineEnd: 0 }}>
      {`${variance.seconds > 0 ? '+' : ''}${dur(variance.seconds)} (${variance.pct > 0 ? '+' : ''}${variance.pct}%)`}
    </Tag>
  );
};
VarianceTag.propTypes = { variance: PropTypes.object };

// ==============================|| PROCESS OPERATIONS MODAL ||============================== //

const ProcessOperationsModal = ({ open, onClose, batchNumber, process }) => {
  const mbmrAlias = useMbmrAlias();
  const ingredientsAlias = useIngredientsAlias();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !process?.process_id) return;
    setLoading(true);
    setData(null);
    setLoadError(false);
    get_batch_process_operations(
      { batch_number: batchNumber, process_id: process.process_id },
      (res) => {
        setLoading(false);
        if (res?.error) {
          setLoadError(true);
          return;
        }
        setData(res?.data || null);
      }
    );
  }, [open, process, batchNumber]);

  const operations = data?.operations || [];
  const processInfo = data?.process || process || {};

  const columns = [
    { title: '#', dataIndex: 'operation_number', key: 'operation_number', width: 50, align: 'center' },
    { title: 'Code', dataIndex: 'operation_code', key: 'operation_code', width: 100, render: (v) => v || '—' },
    {
      title: 'Operation',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 180,
      render: (v) => (
        <Tooltip title={v}>
          <div className="truncate" style={{ maxWidth: 170 }}>{v || '—'}</div>
        </Tooltip>
      ),
    },
    { title: 'Type', dataIndex: 'operation_type', key: 'operation_type', width: 140, render: (v, r) => { const b = opBehaviour(r); return <span><Tag>{typeof v === 'object' ? v?.name || '—' : v || '—'}</Tag>{b !== 'STANDARD' && <Tag color={b === 'UNIT_COUNT' ? 'geekblue' : 'cyan'} style={{ marginTop: 2 }}>{b}</Tag>}</span>; } },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const resolved = resolveOperationExecutionStatus(r.execution?.status);
        return <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>;
      },
    },
    { title: 'Machine', key: 'machine', width: 110, render: (_, r) => r.execution?.machine || '—' },
    { title: 'Scheduled', key: 'scheduled_time', width: 150, render: (_, r) => fmt(r.execution?.scheduled_time) },
    { title: 'Start', key: 'start', width: 150, render: (_, r) => fmt(r.execution?.start_time) },
    { title: 'End', key: 'end', width: 150, render: (_, r) => fmt(r.execution?.end_time) },
    {
      title: 'Standard',
      key: 'standard_duration',
      width: 110,
      render: (_, r) => {
        if (r.hold_min_hours || r.hold_time_type) {
          return (
            <Tooltip title={`${r.hold_time_type || 'Hold'}: ${r.hold_min_hours ?? '—'}–${r.hold_max_hours ?? '—'} h`}>
              <Tag color="purple" style={{ marginInlineEnd: 0 }}>Hold</Tag>
            </Tooltip>
          );
        }
        if (r.standard_duration_seconds === null || r.standard_duration_seconds === undefined) return '—';
        return (
          <Tooltip title={`${mbmrAlias} band: ${r.min_duration || '—'} to ${r.max_duration || '—'}`}>
            {dur(r.standard_duration_seconds)}
          </Tooltip>
        );
      },
    },
    {
      title: 'Actual',
      key: 'duration',
      width: 110,
      render: (_, r) => {
        const actual = r.execution?.actual_duration_seconds;
        if (actual === null || actual === undefined) return '—';
        const outside = r.execution?.within_duration_limits === false;
        return (
          <Tooltip
            title={`Paused: ${formatSeconds(r.execution?.paused_seconds)}${
              outside ? ` · outside the ${r.min_duration}–${r.max_duration} band` : ''
            }`}
          >
            <span style={outside ? { color: 'var(--ant-color-error, #cf1322)', fontWeight: 500 } : undefined}>
              {dur(actual)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Variance',
      key: 'duration_variance',
      width: 130,
      render: (_, r) =>
        (r.execution?.duration_variance ? <VarianceTag variance={r.execution.duration_variance} /> : '—'),
    },
    {
      title: 'Started / Ended By',
      key: 'employees',
      width: 150,
      render: (_, r) => {
        const start = r.execution?.start_employee;
        const end = r.execution?.end_employee;
        const sameEmployee = start && end && start.id === end.id;
        return (
          <div style={{ fontSize: 12 }}>
            <div>{personName(start)}</div>
            {!sameEmployee && end && <div style={{ color: '#64748b' }}>Ended: {personName(end)}</div>}
          </div>
        );
      },
    },
    {
      title: 'Pass/Fail',
      dataIndex: 'is_pass_fail',
      key: 'is_pass_fail',
      width: 90,
      align: 'center',
      render: yesNo,
    },
    { title: 'E-Sign', dataIndex: 'is_esign_required', key: 'is_esign_required', width: 80, align: 'center', render: yesNo },
    {
      title: 'Supervisor Approval',
      key: 'supervisor_approval',
      width: 150,
      render: (_, r) => {
        if (r.supervisor_approval) return <StatusBadge status={r.supervisor_approval.status} />;
        // No approval row yet: the step needs one, but nothing has been requested — not "Pending".
        return r.is_supervisor_approval_required ? <StatusIndicator state="neutral" label="Required" /> : '—';
      },
    },
    {
      title: `${mbmrAlias} Approvals`,
      key: 'recipe_approvals',
      width: 150,
      render: (_, r) =>
        (r.recipe_approvals || []).length === 0
          ? '—'
          : (r.recipe_approvals || []).map((a, i) => (
              <div key={i}>
                <StatusBadge status={a.status} /> <span style={{ fontSize: 12 }}>{a.role}</span>
              </div>
            )),
    },
    {
      title: 'Deviations',
      key: 'deviations',
      width: 110,
      align: 'center',
      render: (_, r) => {
        // ux-gmp-auditor #3: the breakdown now carries the IPC engine's events too — the four legacy
        // buckets are empty for a batch executed through IPC, which is why this used to read
        // "No deviations" on a batch the audit trail reported four for. `open_events` is the release
        // gate's separate count (every correction attempt) and is labelled as such.
        const b = r.deviations?.breakdown;
        const openEvents = r.deviations?.open_events || 0;
        const breakdownText = b
          ? `Parameter: ${b.parameter ?? 0} · ${ingredientsAlias}: ${b.ingredient ?? 0} · Sampling: ${b.sampling ?? 0} · Duration: ${b.duration ?? 0} · IPC/OOS: ${b.ipc ?? 0} · Step: ${b.step ?? 0}${openEvents ? ` · ${openEvents} still-open IPC/OOS event${openEvents === 1 ? '' : 's'}` : ''}${r.deviations?.has_ticket ? ' · Ticket raised' : ''}`
          : 'No deviations on this operation';
        const n = r.deviations?.total || 0;
        // EXV-021: the review register's count for this operation (legacy records, IPC / OOS, runner step
        // deviations); a zero is shown neutral, never as a pass.
        return (
          <StatusIndicator
            state={n ? 'warning' : 'neutral'}
            label={String(n)}
            tooltip={`${breakdownText} · From the review register: legacy deviation records, IPC failures and OOS results, and runner step deviations.`}
          />
        );
      },
    },
    {
      title: 'Pauses/Holds',
      key: 'pauses_holds',
      width: 100,
      align: 'center',
      render: (_, r) => {
        const n = (r.pauses_holds || []).length;
        return <StatusIndicator state={n ? 'hold' : 'neutral'} label={String(n)} />;
      },
    },
    {
      title: 'Details',
      key: 'details',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <span className="action-icon flex items-center justify-center w-full gap-2">
          {opBehaviour(record) === 'UNIT_COUNT' && (
            <Tooltip title="Lot board — tubs, stations, AQL">
              <Button
                type="text"
                size="small"
                aria-label={`Open the lot board for ${record.operation_name || 'this operation'}`}
                icon={<LayoutGrid className="w-5 h-5 text-emerald-600" />}
                onClick={() => navigate(`/inspection/board/${encodeURIComponent(batchNumber)}?op=${record.operation_id ?? ''}`)}
              />
            </Tooltip>
          )}
          {opBehaviour(record) === 'UNIT_COUNT' && (
            <OperatorAppLink type="text" iconOnly batch={batchNumber} operation={record.operation_id} />
          )}
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              aria-label={`View details of ${record.operation_name || 'this operation'}`}
              icon={<Icons.Eye className="w-5 h-5 text-blue-600" />}
              onClick={() => setSelectedOperation(record)}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageModal
        icon={<Icons.Workflow size={22} />}
        title={processInfo.process_name || 'Process Operations'}
        meta={processInfo.process_code ? <Tag>{processInfo.process_code}</Tag> : null}
        description="Operations of this process with their standard and actual durations."
        open={open && !selectedOperation}
        onCancel={onClose}
        width="min(90vw, 1400px)"
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" tip="Loading operations…" />
          </div>
        ) : loadError ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Couldn't load operation details. Please try again."
            />
          </div>
        ) : (
          <>
            <Card size="small" className="mb-3">
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="Standard duration">
                  <Tooltip title={`${mbmrAlias} standard: the sum of this process's operation standards (holds excluded)`}>
                    {dur(processInfo.duration_seconds ?? processInfo.standard_duration_seconds)}
                  </Tooltip>
                </Descriptions.Item>
                <Descriptions.Item label="Actual duration">
                  <Tooltip title={`Σ of the operations that have run, paused time excluded${
                    processInfo.actual_elapsed_seconds ? ` · elapsed ${dur(processInfo.actual_elapsed_seconds)}` : ''
                  }`}
                  >
                    {dur(processInfo.actual_duration_seconds)}
                  </Tooltip>
                </Descriptions.Item>
                <Descriptions.Item label="Variance">
                  {!processInfo.duration_variance ? (
                    '—'
                  ) : processInfo.is_complete === false ? (
                    <Tooltip title={`Only ${processInfo.operations_completed} of ${processInfo.operations_total} operations have run — the variance is meaningful once the process completes`}>
                      <Tag style={{ marginInlineEnd: 0 }}>In progress</Tag>
                    </Tooltip>
                  ) : (
                    <VarianceTag variance={processInfo.duration_variance} />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Batch Number">{data?.batch_number || batchNumber}</Descriptions.Item>
                <Descriptions.Item label="Scheduled Start">{fmt(processInfo.scheduled_start ?? processInfo.schedule?.start_dt)}</Descriptions.Item>
                <Descriptions.Item label="Scheduled End">{fmt(processInfo.scheduled_end ?? processInfo.schedule?.end_dt)}</Descriptions.Item>
              </Descriptions>
            </Card>
            <DataTable
              tableKey="batchProcessOperations"
              searchable
              searchPlaceholder="Search operations…"
              columnConfig
              showCount={false}
              bordered
              data={operations.map((op, i) => ({ ...op, key: op.operation_id ?? i }))}
              columns={columns}
              pagination={false}
              // Responsive: was 480px flat, anchored to a 900px reference viewport so it scales on larger/smaller screens
              scrollX="max-content"
              height={null}
              minHeight={240}
              locale={{ emptyText: <Empty description="No operations found for this process" /> }}
            />
          </>
        )}
      </PageModal>

      <PageModal
        icon={<Icons.ClipboardList size={22} />}
        title={selectedOperation?.operation_name || 'Operation Details'}
        meta={selectedOperation?.operation_code ? <Tag>{selectedOperation.operation_code}</Tag> : null}
        description="Everything recorded for this operation during execution."
        open={!!selectedOperation}
        onCancel={() => setSelectedOperation(null)}
        width="min(85vw, 1200px)"
        destroyOnHidden
      >
        <div className="space-y-3">
          {(selectedOperation?.pauses_holds || []).length > 0 && (
            <DataTable
              title="Pauses / Holds"
              height={null}
              data={(selectedOperation.pauses_holds || []).map((p, i) => ({ ...p, key: i }))}
              pagination={false}
              columns={[
                { title: 'Action', dataIndex: 'action', key: 'action', render: (v) => <Tag color="orange">{v || '—'}</Tag> },
                { title: 'Started At', dataIndex: 'started_at', key: 'started_at', render: fmt },
                { title: 'Ended At', dataIndex: 'ended_at', key: 'ended_at', render: fmt },
                { title: 'Duration', dataIndex: 'duration_seconds', key: 'duration_seconds', render: (v) => formatSeconds(v) },
                { title: 'Reason', dataIndex: 'reason', key: 'reason', render: (v) => v || '—' },
                { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
                { title: 'Started By', dataIndex: 'started_by', key: 'started_by', render: personName },
              ]}
            />
          )}
          {selectedOperation && (
            <OperationDetailPanel batchNumber={batchNumber} operationId={selectedOperation.operation_id} />
          )}
        </div>
      </PageModal>
    </>
  );
};

ProcessOperationsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  batchNumber: PropTypes.string,
  process: PropTypes.object,
};

export default ProcessOperationsModal;
